/**
 * Conversion entre la hora de pared del consultorio y el instante que se
 * guarda.
 *
 * Vive en los contratos, no en el backend, porque los dos lados lo necesitan:
 * el servidor para calcular la disponibilidad y guardar el instante correcto, y
 * la interfaz para pintar la agenda en la hora que el medico tiene en la pared
 * y para convertir el hueco que se eligio en el instante que se manda. Dos
 * implementaciones de esto discreparian en el cambio de horario de verano, que
 * es justo cuando mas caro sale.
 *
 * No hay dependencia nueva: `Intl` sabe de zonas horarias desde el motor.
 */
const formateadores = new Map();
function formateador(zona) {
    let existente = formateadores.get(zona);
    if (!existente) {
        existente = new Intl.DateTimeFormat('en-CA', {
            timeZone: zona,
            // `hourCycle` explicito: con `hour12: false` a secas, algunos motores
            // devuelven «24» para la medianoche.
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        formateadores.set(zona, existente);
    }
    return existente;
}
function partesEnZona(instante, zona) {
    const partes = formateador(zona).formatToParts(instante);
    const valor = (tipo) => {
        const parte = partes.find((candidata) => candidata.type === tipo);
        return Number(parte?.value ?? 0);
    };
    return {
        anio: valor('year'),
        mes: valor('month'),
        dia: valor('day'),
        hora: valor('hour'),
        minuto: valor('minute'),
        segundo: valor('second'),
    };
}
/**
 * Desfase de la zona en ese instante, en minutos respecto de UTC.
 *
 * Positivo al este de Greenwich. Para la Ciudad de Mexico son -360.
 */
export function desfaseEnMinutos(instante, zona) {
    const partes = partesEnZona(instante, zona);
    const comoSiFueraUtc = Date.UTC(partes.anio, partes.mes - 1, partes.dia, partes.hora, partes.minuto, partes.segundo);
    // Se descartan los milisegundos del instante: el desfase de una zona siempre
    // es un numero entero de minutos.
    const instanteSinMilisegundos = Math.floor(instante.getTime() / 1000) * 1000;
    return Math.round((comoSiFueraUtc - instanteSinMilisegundos) / 60_000);
}
/**
 * Instante que corresponde a una hora de pared del consultorio.
 *
 * Dos pasadas a proposito. La primera calcula el desfase sobre un instante que
 * todavia es el equivocado; en un domingo de cambio de horario ese desfase no
 * es el que aplica, y la segunda pasada lo corrige. Con una sola, dos veces al
 * año las citas de esa madrugada quedan una hora movidas.
 */
export function instanteDeHoraLocal(fecha, hora, zona) {
    const tentativo = new Date(`${fecha}T${hora}:00Z`);
    if (Number.isNaN(tentativo.getTime())) {
        throw new Error(`Fecha u hora inválida: ${fecha} ${hora}`);
    }
    const primera = new Date(tentativo.getTime() - desfaseEnMinutos(tentativo, zona) * 60_000);
    return new Date(tentativo.getTime() - desfaseEnMinutos(primera, zona) * 60_000);
}
/** Dia del calendario del consultorio, en AAAA-MM-DD. */
export function fechaLocal(instante, zona) {
    const partes = partesEnZona(instante, zona);
    return [
        String(partes.anio).padStart(4, '0'),
        String(partes.mes).padStart(2, '0'),
        String(partes.dia).padStart(2, '0'),
    ].join('-');
}
/** Hora de pared del consultorio, en HH:MM. */
export function horaLocal(instante, zona) {
    const partes = partesEnZona(instante, zona);
    return `${String(partes.hora).padStart(2, '0')}:${String(partes.minuto).padStart(2, '0')}`;
}
/**
 * Suma dias de calendario a una fecha AAAA-MM-DD.
 *
 * Aritmetica de calendario pura, hecha en UTC para que no dependa de la zona de
 * la maquina: el 30 de octubre mas un dia es el 31, cambie o no el horario esa
 * noche.
 */
export function sumarDias(fecha, dias) {
    const [anio, mes, dia] = fecha.split('-').map(Number);
    const movido = new Date(Date.UTC(anio ?? 1970, (mes ?? 1) - 1, (dia ?? 1) + dias));
    return movido.toISOString().slice(0, 10);
}
/** Comprueba que la zona exista antes de guardarla como configuracion. */
export function zonaHorariaValida(zona) {
    try {
        new Intl.DateTimeFormat('en-CA', { timeZone: zona });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Si el instante cae dentro de un horario de silencio (horas de pared del
 * consultorio, `desde` incluido y `hasta` excluido), el instante en que
 * termina; si no, `null`. Un silencio que cruza la medianoche (22:00 a 08:00)
 * termina al dia siguiente cuando se esta antes de la medianoche.
 */
export function finDelSilencio(instante, silencio, zona) {
    const ahora = horaLocal(instante, zona);
    const hoy = fechaLocal(instante, zona);
    const cruzaMedianoche = silencio.desde > silencio.hasta;
    if (cruzaMedianoche) {
        if (ahora >= silencio.desde) {
            return instanteDeHoraLocal(sumarDias(hoy, 1), silencio.hasta, zona);
        }
        if (ahora < silencio.hasta)
            return instanteDeHoraLocal(hoy, silencio.hasta, zona);
        return null;
    }
    if (ahora >= silencio.desde && ahora < silencio.hasta) {
        return instanteDeHoraLocal(hoy, silencio.hasta, zona);
    }
    return null;
}
//# sourceMappingURL=tiempo.js.map